import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Button } from "reactstrap";

const SLA_FIELDS = [
  { key: "critical", label: "Critical" },
  { key: "high", label: "High" },
  { key: "medium", label: "Medium" },
  { key: "low", label: "Low" },
];

/**
 * Shows the organization's remediation SLA (days per severity).
 * Tenant admins can edit it or reset it to platform defaults.
 */
const SlaPolicyCard = ({ apiBase, token, isAdmin, onFeedback }) => {
  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadPolicy = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}sla/policy`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.status) {
        setPolicy(data);
      }
    } catch (err) {
      console.error("[SlaPolicyCard load Error]", err);
    }
  }, [apiBase, token]);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const startEditing = () => {
    setDraft({ ...policy.days });
    setEditing(true);
    setExpanded(true);
  };

  const submit = async (method) => {
    setSaving(true);
    try {
      const response = await fetch(`${apiBase}sla/policy`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: method === "PUT"
          ? JSON.stringify({ days: Object.fromEntries(SLA_FIELDS.map((f) => [f.key, Number(draft[f.key])])) })
          : undefined,
      });
      const data = await response.json();
      if (data.status) {
        setPolicy(data);
        setEditing(false);
        onFeedback({
          type: "success",
          message: method === "PUT"
            ? "SLA policy saved. It applies to findings created from now on."
            : "SLA policy reset to platform defaults.",
        });
      } else {
        onFeedback({ type: "danger", message: data.error || "Failed to update SLA policy" });
      }
    } catch (err) {
      onFeedback({ type: "danger", message: err.message || "Network error" });
    } finally {
      setSaving(false);
    }
  };

  if (!policy) return null;

  return (
    <div className="sla-policy-card">
      <div className="sla-policy-head">
        <button className="sla-policy-toggle" onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
            style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>
            <path d="m9 18 6-6-6-6" />
          </svg>
          <span className="sla-policy-title">Remediation SLA</span>
          <span className={`sla-policy-badge ${policy.isCustom ? "custom" : ""}`}>
            {policy.isCustom ? "Custom" : "Default"}
          </span>
        </button>
        {!expanded && (
          <div className="sla-policy-summary">
            {SLA_FIELDS.map((f) => (
              <span key={f.key}>{f.label} <strong>{policy.days[f.key]}d</strong></span>
            ))}
          </div>
        )}
      </div>

      {expanded && (
        <div className="sla-policy-body">
          <div className="sla-policy-grid">
            {SLA_FIELDS.map((f) => (
              <div className="sla-policy-field" key={f.key}>
                <label htmlFor={`sla-${f.key}`}>{f.label}</label>
                {editing ? (
                  <input
                    id={`sla-${f.key}`}
                    type="number"
                    min={1}
                    max={365}
                    value={draft[f.key] ?? ""}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                ) : (
                  <div className="sla-policy-value">
                    {policy.days[f.key]} days
                    {policy.isCustom && policy.defaults[f.key] !== policy.days[f.key] && (
                      <span className="sla-policy-default">default {policy.defaults[f.key]}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="sla-policy-footer">
            <span className="sla-policy-note">
              Changes apply to new findings only. Existing due dates stay as they are.
              {policy.updatedBy && ` Last changed by ${policy.updatedBy}.`}
            </span>
            {isAdmin && (
              <div className="sla-policy-actions">
                {editing ? (
                  <>
                    <Button color="secondary" outline size="sm" disabled={saving} onClick={() => setEditing(false)}>
                      Cancel
                    </Button>
                    <Button color="primary" size="sm" disabled={saving} onClick={() => submit("PUT")}>
                      {saving ? <Spinner size="sm" /> : "Save SLA"}
                    </Button>
                  </>
                ) : (
                  <>
                    {policy.isCustom && (
                      <Button color="secondary" outline size="sm" disabled={saving} onClick={() => submit("DELETE")}>
                        Reset to defaults
                      </Button>
                    )}
                    <Button color="primary" size="sm" onClick={startEditing}>
                      Edit SLA
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SlaPolicyCard;
